import { NewConvertSection } from './memberTypes';

export const newConvertGuideEn: NewConvertSection[] = [
  {
    id: 'bienvenida-al-reino',
    title: 'Welcome to the Kingdom: What It Means to Be a New Member',
    content: `
Becoming a new member is not the end of a journey—it is the beginning of a transformed life. You have entered through the gate (2 Nephi 31), and now you begin the path of discipleship.

This path requires:

- seeing yourself as a child of God,
- learning to recognize the Spirit,
- integrating into a covenant community,
- strengthening your faith through daily habits.

The Church is a spiritual family, not a social club. Your growth matters. Your questions matter. Your story matters.
    `,
    bullets: [
      'Your baptism is a real covenant with God.',
      'The Holy Ghost becomes your constant companion as you seek His influence.',
      'Your path is individual but never solitary.',
      'You have a place in the Kingdom: you are part of the gathering of Israel.',
    ],
    scriptures: [
      { ref: '2 Nephi 31:17–20' },
      { ref: 'John 3:5' },
      { ref: 'Mosiah 18:8–10' },
    ],
    tips: [
      "Don’t compare your progress to others.",
      'Keep a spiritual journal for the first 90 days.',
      'Pray daily specifically to feel the companionship of the Spirit.',
    ],
  },
  {
    id: 'integracion-al-barrio',
    title: 'Integrating Into the Ward and Creating Spiritual Belonging',
    content: `
A new convert flourishes when they develop friendships, spiritual connections, and real opportunities to serve. Belonging comes before behavior.

Integration is not automatic—it is nurtured through your desire to participate and the ward’s willingness to embrace you.

Three pillars support your integration:

1. **Relationships** — Sincere friendships that strengthen your faith.
2. **Spiritual support** — People who walk with you in your early steps.
3. **Opportunities to serve** — Small assignments that strengthen your identity as a covenant disciple.
    `,
    bullets: [
      'Sit with a different family every Sunday.',
      'Attend ward activities—even simple ones.',
      'Share your questions openly with your leaders.',
      'Accept invitations to home evenings, scripture study or visits.',
    ],
    scriptures: [
      { ref: 'Moroni 6:4–5' },
      { ref: 'Acts 2:42–47' },
    ],
    tips: [
      'Identify two people you feel spiritually safe with.',
      'Request a priesthood blessing if you need strength.',
      'Practice sharing your conversion in 2 minutes.',
    ],
  },
  {
    id: 'sacerdocio-aaronico',
    title: 'Introduction to the Aaronic Priesthood',
    content: `
If you are a male and at least 12 years old, you may receive the Aaronic Priesthood. New male converts are often ordained to this priesthood before receiving the Melchizedek Priesthood.

The Aaronic Priesthood is:

- preparatory,
- centered on service,
- focused on the sacrament,
- a place to learn reverence, responsibility and purity.

The key is **worthiness**, not perfection.
    `,
    bullets: [
      'You will learn about the sacrament and how it is prepared.',
      'You will participate in quorum meetings.',
      'You will serve in simple assignments.',
      'You will develop habits of prayer, study, and obedience.',
    ],
    scriptures: [
      { ref: 'D&C 20:46–59' },
      { ref: '1 Samuel 3' },
    ],
    tips: [
      'Ask for a spiritual mentor within the quorum.',
      'Learn the sacrament prayers by heart.',
      'Ask what it means to serve with reverence.',
    ],
  },
  {
    id: 'sacerdocio-melquisedec',
    title: 'Preparing for the Melchizedek Priesthood',
    content: `
The Melchizedek Priesthood is a sacred step. It requires spiritual maturity, a strong testimony of Jesus Christ, and a pattern of ongoing repentance.

This priesthood enables you to:

- bless,
- comfort,
- guide spiritually,
- minister with power,
- prepare your family for temple blessings.

Your bishop and stake president will guide you in this process.
    `,
    bullets: [
      'Develop a daily pattern of repentance.',
      'Speak with your bishop about your preparation.',
      'Participate in ministering with your assigned companion.',
      'Serve proactively without waiting for invitations.',
    ],
    scriptures: [
      { ref: 'D&C 84:33–44' },
      { ref: 'D&C 107' },
    ],
    tips: [
      'Record impressions from the Holy Ghost.',
      'Identify your spiritual gifts.',
      'Ask God to guide your preparation to lead in your home.',
    ],
  },
  {
    id: 'primer-visita-al-templo',
    title: 'Your First Visit to the Temple',
    content: `
Temples are holy houses of the Lord. New members may enter the temple to perform **baptisms for the dead** shortly after baptism, as long as they have a limited-use recommend.

In the temple you will feel:

- deep peace,
- spiritual clarity,
- renewed purpose,
- a profound sense of belonging.

Temple workers will guide you every step—you will not be alone.
    `,
    bullets: [
      'You will wear white clothing as a symbol of purity.',
      'You will participate in proxy ordinances for your ancestors.',
      'You will hear comforting and uplifting words.',
      'You will feel the influence of the Holy Ghost.',
    ],
    scriptures: [
      { ref: 'D&C 124:39' },
      { ref: '1 Corinthians 15:29' },
    ],
    tips: [
      'Pray for peace before your visit.',
      'Go with your missionaries or your leader.',
      'Write in your journal what you felt.',
    ],
  },
  {
    id: 'recomendacion-templo',
    title: 'The Temple Recommend and Worthiness',
    content: `
Receiving a temple recommend is a sacred step. The questions are not examinations—they are a spiritual conversation between you and God through His servants.

You will be asked about:

- your faith in Jesus Christ,
- your repentance,
- your obedience,
- your loyalty to covenants,
- your honesty,
- your moral life,
- your relationship with the Spirit.

You do not need to be perfect. You need to be honest, humble, and striving every day.
    `,
    bullets: [
      'Schedule your appointment with your bishop.',
      'Be completely honest and transparent.',
      'Share both your progress and your concerns.',
      'Remember that God is more merciful than strict.',
    ],
    scriptures: [
      { ref: 'Psalms 24:3–4' },
      { ref: 'D&C 97:8' },
    ],
    tips: [
      'Do a weekly worthiness self-check.',
      'Do not hesitate to ask questions.',
      'Go with a willingness to obey and learn.',
    ],
  },
  {
    id: 'primeros-90-dias',
    title: 'Your First 90 Days: A Spiritual Growth Path',
    content: `
The first 90 days are critical. What you do during this period will greatly determine your spiritual growth and stability in the Church.

Your recommended path:

- Weeks 1–4: Strengthen spiritual habits.
- Weeks 5–8: Integrate into the ward.
- Weeks 9–12: Serve and prepare for the temple.
    `,
    bullets: [
      'Read the Book of Mormon at least 10 minutes daily.',
      'Attend church consistently for 8 weeks.',
      'Pray by name for your ministers and leaders.',
      'Participate in at least one service activity.',
    ],
    scriptures: [
      { ref: 'Mosiah 5' },
      { ref: '3 Nephi 18:18–20' },
    ],
    tips: [
      'Ask for help when you need it.',
      'Be patient with your growth.',
      'Set one spiritual goal per month.',
    ],
  },
];

